import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Download, Printer, Mail } from 'lucide-react';

const NoteAuteurView = () => {
  const { id } = useParams();
  const [note, setNote] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  // Add print styles
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        @page {
          size: A4;
          margin: 15mm;
        }
        
        body {
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        }
        
        .no-print {
          display: none !important;
        }
        
        .print\\:shadow-none {
          box-shadow: none !important;
        }
        
        .print\\:rounded-none {
          border-radius: 0 !important;
        }
        
        .p-8 {
          padding: 20px !important;
        }
        
        table {
          page-break-inside: avoid;
        }
        
        .border-b {
          page-break-after: avoid;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    loadNote();
  }, [id]);

  const loadNote = async () => {
    try {
      const res = await axios.get(`${API}/notes-auteur/${id}`, { withCredentials: true });
      setNote(res.data);
      
      // Load client
      const clientRes = await axios.get(`${API}/clients/${res.data.client_id}`, { withCredentials: true });
      setClient(clientRes.data);
    } catch (error) {
      console.error('Error loading note:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    try {
      const url = `${API}/notes-auteur/${id}/pdf`;
      const response = await axios.get(url, {
        withCredentials: true,
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const numero = note?.numero || id;
      link.href = downloadUrl;
      link.download = `Note d'Auteur ${numero}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading note auteur PDF:', error);
      alert('Erreur lors du téléchargement du PDF');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!note || !client) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-stone-600 mb-4">Note d'auteur non trouvée</p>
          <Link to="/backoffice/notes-auteur">
            <Button variant="outline">Retour aux notes</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 py-8">
      {/* Action buttons - hidden on print */}
      <div className="no-print max-w-4xl mx-auto px-4 mb-4 flex justify-between items-center">
        <Link to="/backoffice/notes-auteur">
          <Button variant="ghost">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
        </Link>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Imprimer
          </Button>
          <Button variant="outline" onClick={handleDownloadPDF}>
            <Download className="h-4 w-4 mr-2" />
            Télécharger PDF
          </Button>
        </div>
      </div>

      {/* Document */}
      <div className="max-w-4xl mx-auto bg-white shadow-lg print:shadow-none print:rounded-none">
        <div className="p-8">
          {/* Header */}
          <div className="flex justify-between items-start mb-8 pb-6 border-b-2 border-amber-600">
            <div>
              <h1 className="text-2xl font-bold text-stone-800">{note.emetteur?.nom || 'FERRARA Jeremy'}</h1>
              <p className="text-stone-600 text-sm mt-1">{note.emetteur?.adresse || '6, Rue du Pic de Barrette'}</p>
              <p className="text-stone-600 text-sm">{note.emetteur?.ville || '75015 Paris'}</p>
              <p className="text-stone-600 text-sm">{note.emetteur?.telephone || '06.68.11.93.43'}</p>
            </div>
            <div className="text-right">
              <h2 className="text-lg font-semibold text-stone-800">{client.prenom} {client.nom}</h2>
              <p className="text-stone-600 text-sm mt-1">{client.adresse}</p>
              <p className="text-stone-600 text-sm">{client.code_postal} {client.ville}</p>
            </div>
          </div>

          {/* Date & Objet */}
          <div className="flex justify-end mb-4 text-sm">
            <div className="text-right">
              <p className="text-stone-600"><strong>Date:</strong> {note.date}</p>
              <p className="text-stone-600"><strong>Objet:</strong> {note.objet}</p>
            </div>
          </div>

          {/* Title */}
          <div className="text-center my-8">
            <h1 className="text-3xl font-bold text-amber-700 uppercase">Note d'Auteur</h1>
            <p className="text-xl font-semibold text-stone-700 mt-2">N° {note.numero}</p>
          </div>

          {/* Échéance */}
          <div className="mb-6">
            <p className="text-stone-700"><strong>Échéance:</strong> {note.echeance}</p>
          </div>

          {/* Table */}
          <table className="w-full mb-6">
            <thead>
              <tr className="bg-amber-50 border-t-2 border-b-2 border-amber-600">
                <th className="text-left p-3 font-semibold text-stone-800">INTITULE</th>
                <th className="text-right p-3 font-semibold text-stone-800 w-32">SOMME</th>
              </tr>
            </thead>
            <tbody>
              {note.lines?.map((line, index) => (
                <tr key={index} className="border-b border-stone-200">
                  <td className="p-3 text-stone-700">{line.description}</td>
                  <td className="p-3 text-right font-mono text-stone-800">{line.montant_ttc.toFixed(2)}€</td>
                </tr>
              ))}
              
              {/* Total TTC */}
              <tr className="bg-amber-50 border-t-2 border-amber-200">
                <td className="p-3 font-bold text-stone-800">TOTAL TTC</td>
                <td className="p-3 text-right font-mono font-bold text-stone-900">{note.total_ttc?.toFixed(2)}€</td>
              </tr>

              {/* CSG/CRDS */}
              <tr className="bg-stone-50">
                <td className="p-3 text-stone-700">CSG/CRDS</td>
                <td className="p-3 text-right font-mono text-stone-700">{note.csg_crds_percent}%</td>
              </tr>
              <tr className="bg-stone-50">
                <td className="p-3 text-stone-700"></td>
                <td className="p-3 text-right font-mono text-stone-700">-{note.csg_crds_amount?.toFixed(2)}€</td>
              </tr>

              {/* Part à régler */}
              <tr className="bg-stone-50 border-b border-stone-300">
                <td className="p-3 font-semibold text-stone-800">PART À RÉGLER</td>
                <td className="p-3 text-right font-mono font-semibold text-stone-900">{note.part_a_regler?.toFixed(2)}€</td>
              </tr>

              {/* AGESSA */}
              <tr className="bg-stone-50">
                <td className="p-3 text-stone-700">AGESSA part auteur</td>
                <td className="p-3 text-right font-mono text-stone-700">{note.agessa_percent}%</td>
              </tr>
              <tr className="bg-stone-50">
                <td className="p-3 text-stone-700"></td>
                <td className="p-3 text-right font-mono text-stone-700">-{note.agessa_amount?.toFixed(2)}€</td>
              </tr>
              <tr className="bg-stone-50">
                <td className="p-3 text-stone-700">AGESSA fixe</td>
                <td className="p-3 text-right font-mono text-stone-700">-{note.agessa_fixe?.toFixed(2)}€</td>
              </tr>

              {/* Net à payer */}
              <tr className="bg-green-700 text-white border-t-2 border-green-800">
                <td className="p-4 font-bold text-lg">NET À PAYER</td>
                <td className="p-4 text-right font-mono font-bold text-xl">{note.net_a_payer?.toFixed(2)}€</td>
              </tr>
            </tbody>
          </table>

          {/* Mentions légales */}
          <div className="mt-8 p-4 bg-stone-50 border border-stone-200 rounded text-xs text-stone-600 leading-relaxed">
            « Rémunération au titre de droits d'auteur occasionnels – à déclarer en traitements et salaires. 
            Pas assujetti à la TVA – article 293 B du CGI. Les cotisations sociales sont précomptées par le 
            diffuseur conformément à l'article L.382-4 du Code de la sécurité sociale. »
          </div>

          {/* Footer */}
          <div className="mt-6 text-sm text-stone-600">
            Règlement à la réception de la note d'auteur, RIB joint.
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoteAuteurView;
